// src/app/resources/tools/quizzes/[slug]/error.tsx

'use client'

import { useEffect } from 'react'

import { handleClientError } from '~/lib/client-error-handling'

/**
 * Quiz route-segment error boundary — catches render errors in the quiz page
 * subtree (layout, analytics, ResourceLayout) that do not reach the widget
 * boundary inside `<Quiz>`. Logs to the drain with classification and GA4.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}): JSX.Element {
  useEffect(() => {
    // GA4 mirroring happens inside `logClientError` (via handleClientError)
    // after the drain confirms the ingest — `component`/`fatal`/`digest` from
    // this context carry through to the `exception` beacon. No direct
    // `trackException` here, or one crash would emit two GA4 exceptions.
    handleClientError(
      error,
      {
        type: 'route_error',
        component: 'quiz-error',
        fatal: false,
        digest: error.digest,
      },
      { boundaryRecover: true },
    )
  }, [error])

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
      <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
        Error Loading Quiz
      </h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Something went wrong while loading the quiz. Please try again later.
      </p>
      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={() => reset()}
          className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Try Again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:hover:bg-gray-600"
        >
          Reload page
        </button>
      </div>
    </div>
  )
}
